import React, { useEffect, useMemo, useState } from 'react';
import 'react-vertical-timeline-component/style.min.css';
import { Button } from '@shared/UI';
import { useAsyncData } from '@shared/react';
import { ITimeLineActionsProps } from './';
import { postStart, postSetCompleted, postManualApprove, getIsWaiting } from '../../api';
import './TimelineActions.scss';
import { useDailySettlement } from '@shared/providers/DailySettlementProvider';

export const TimelineActions: React.FC<ITimeLineActionsProps> = ({ status, callback, setNeedApproval }) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { refreshDailySettlement } = useDailySettlement();
    const needCheckWaiting = status.status === 'Working';
    const { data: isWaiting } = useAsyncData(
        async () => {
            if (needCheckWaiting) {
                return await getIsWaiting({ name: status.name });
            }
        },
        false,
        [],
    );

    const callStart = (name: string) => {
        setIsLoading(true);
        postStart({ name }).finally(() => {
            setIsLoading(false);
            refreshDailySettlement();
        });
    };

    useEffect(() => {
        if (isWaiting && setNeedApproval) {
            setNeedApproval((prev) => [...prev, status.name]);
        }
    }, [isWaiting]);

    const actions = useMemo(
        () => [
            {
                name: 'Restart Step',
                action: callStart,
                availableInStatuses: ['Error'],
            },
            {
                name: 'Force Complete',
                action: (name: string) => {
                    setIsLoading(true);
                    postSetCompleted({ name }).finally(() => {
                        setIsLoading(false);
                        refreshDailySettlement();
                    });
                },
                availableInStatuses: ['Error'],
            },
            {
                name: 'Start Step',
                action: callStart,
                availableInStatuses: ['NotRun'],
            },
            {
                name: 'Complete Step',
                action: (name: string) => {
                    setIsLoading(true);
                    postManualApprove({ name }).finally(() => {
                        setIsLoading(false);
                        refreshDailySettlement();
                        if (isWaiting && setNeedApproval) {
                            setNeedApproval((prev) => prev.filter((step) => step !== status.name));
                        }
                    });
                },
                availableInStatuses: ['Working'],
            },
        ],
        [],
    );

    const isWaitingCondition = !needCheckWaiting || isWaiting;

    return (
        <div className='timeline-actions'>
            {actions.map(
                (action) =>
                    action.availableInStatuses.includes(status.status) &&
                    isWaitingCondition && (
                        <Button
                            key={action.name}
                            className='timeline-actions__button'
                            disabled={isLoading}
                            onClick={() => {
                                action.action(status.name);
                                if (callback) {
                                    callback();
                                }
                            }}
                        >
                            {action.name}
                        </Button>
                    ),
            )}
        </div>
    );
};
